import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, GripVertical, Save } from "lucide-react";
import { toast } from "sonner";
import { SCHEMES } from "@/lib/constants";

const QTYPES = [
  { v: "boolean", l: "Yes / No / N/A" },
  { v: "text", l: "Short text" },
  { v: "textarea", l: "Long text" },
  { v: "number", l: "Number" },
  { v: "select", l: "Dropdown" },
];

const uid = () => Math.random().toString(36).slice(2, 10);

export default function AdminMeasures() {
  const [measures, setMeasures] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newM, setNewM] = useState({ code: "", name: "", description: "" });

  const load = () => api.get("/measures").then((r) => setMeasures(r.data));
  useEffect(() => {
    load();
  }, []);

  const saveMeasure = async (m) => {
    await api.put(`/measures/${m.id}`, m);
    toast.success(`${m.name} saved`);
    load();
  };

  const deleteMeasure = async (m) => {
    if (!window.confirm(`Delete measure "${m.name}"?`)) return;
    await api.delete(`/measures/${m.id}`);
    toast.success("Measure deleted");
    load();
  };

  const addMeasure = async () => {
    if (!newM.code || !newM.name) return toast.error("Code and name required");
    await api.post("/measures", {
      id: uid(),
      code: newM.code,
      name: newM.name,
      description: newM.description,
      order: (measures?.length || 0) + 1,
      active: true,
      schemes: SCHEMES,
      questions: [],
    });
    setAddOpen(false);
    setNewM({ code: "", name: "", description: "" });
    toast.success("Measure added");
    load();
  };

  if (!measures)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-accent" />
      </div>
    );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Configuration</p>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Measures</h1>
          <p className="mt-1 text-sm text-slate-500">Edit measures, questions and follow-ups used in site visit forms.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="field-lg bg-accent font-bold hover:bg-accent/90" data-testid="add-measure-btn">
          <Plus className="mr-2 h-4 w-4" /> Add measure
        </Button>
      </div>

      <Accordion type="multiple" className="space-y-3">
        {measures.map((m) => (
          <MeasureEditor key={m.id} measure={m} onSave={saveMeasure} onDelete={deleteMeasure} />
        ))}
      </Accordion>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add measure</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input value={newM.code} onChange={(e) => setNewM({ ...newM, code: e.target.value.toUpperCase() })} placeholder="e.g. IWI" className="field-lg" data-testid="new-measure-code" />
            </div>
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={newM.name} onChange={(e) => setNewM({ ...newM, name: e.target.value })} placeholder="e.g. Internal Wall Insulation" className="field-lg" data-testid="new-measure-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={newM.description} onChange={(e) => setNewM({ ...newM, description: e.target.value })} className="field-lg" data-testid="new-measure-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={addMeasure} className="bg-accent font-bold hover:bg-accent/90" data-testid="save-new-measure-btn">Add measure</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MeasureEditor({ measure, onSave, onDelete }) {
  const [m, setM] = useState(measure);
  useEffect(() => setM(measure), [measure]);

  const set = (patch) => setM((x) => ({ ...x, ...patch }));
  const setQ = (qid, patch) => set({ questions: m.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)) });
  const addQ = () => set({ questions: [...m.questions, { id: uid(), label: "", type: "boolean", options: [], required: false }] });
  const delQ = (qid) => set({ questions: m.questions.filter((q) => q.id !== qid) });

  return (
    <AccordionItem value={m.id} className="rounded-md border border-slate-200 bg-white px-4">
      <AccordionTrigger className="hover:no-underline" data-testid={`measure-acc-${m.code}`}>
        <div className="flex items-center gap-3 text-left">
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{m.code}</span>
          <span className="font-heading font-bold text-slate-900">{m.name}</span>
          <span className="text-xs text-slate-400">{m.questions.length} questions</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-5 pb-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code</Label>
            <Input value={m.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} className="field-lg" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
            <Input value={m.name} onChange={(e) => set({ name: e.target.value })} className="field-lg" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
            <Input value={m.description || ""} onChange={(e) => set({ description: e.target.value })} className="field-lg" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={m.active} onCheckedChange={(v) => set({ active: v })} />
            <span className="text-sm font-semibold text-slate-700">Active in forms</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!m.has_rooms} onCheckedChange={(v) => set({ has_rooms: v })} />
            <span className="text-sm font-semibold text-slate-700">Room-by-room section</span>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Schemes this measure applies to</Label>
            <div className="flex flex-wrap gap-2">
              {SCHEMES.map((s) => {
                const on = (m.schemes || []).includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set({ schemes: on ? (m.schemes || []).filter((x) => x !== s) : [...(m.schemes || []), s] })}
                    className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "border-accent bg-accent/10 text-accent" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`}
                    data-testid={`scheme-${m.code}-${s.slice(0, 6)}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Photo evidence prompts (one per line)</Label>
            <Textarea
              value={(m.photo_prompts || []).join("\n")}
              onChange={(e) => set({ photo_prompts: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              className="min-h-[90px]"
              placeholder={"Fixings\nStructural integrity\nInsulation continuity"}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Questions</Label>
            <Button size="sm" variant="outline" onClick={addQ} className="border-slate-300 font-bold" data-testid={`add-question-${m.code}`}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Question
            </Button>
          </div>
          <div className="mt-3 space-y-3">
            {m.questions.map((q) => (
              <div key={q.id} className="rounded-md border border-slate-200 p-3">
                <div className="flex items-start gap-2">
                  <GripVertical className="mt-2.5 h-4 w-4 shrink-0 text-slate-300" />
                  <div className="flex-1 space-y-2">
                    <Input value={q.label} onChange={(e) => setQ(q.id, { label: e.target.value })} placeholder="Question label" className="field-lg" />
                    <div className="flex flex-wrap items-center gap-3">
                      <Select value={q.type} onValueChange={(v) => setQ(q.id, { type: v })}>
                        <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {QTYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 text-sm text-slate-600">
                        <Switch checked={q.required} onCheckedChange={(v) => setQ(q.id, { required: v })} /> Required
                      </label>
                      {q.type === "select" && (
                        <Input
                          value={(q.options || []).join(", ")}
                          onChange={(e) => setQ(q.id, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                          placeholder="Option 1, Option 2, …"
                          className="h-9 flex-1 min-w-[200px]"
                        />
                      )}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => delQ(q.id)} className="h-8 w-8 text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between border-t border-slate-100 pt-4">
          <Button variant="ghost" onClick={() => onDelete(m)} className="text-destructive" data-testid={`delete-measure-${m.code}`}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete measure
          </Button>
          <Button onClick={() => onSave(m)} className="bg-accent font-bold hover:bg-accent/90" data-testid={`save-measure-${m.code}`}>
            <Save className="mr-2 h-4 w-4" /> Save changes
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
